import { useState, useEffect } from 'react'
import { adminAPI } from '../../lib/api'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import type { User } from '../../types'
import { useToast } from '../../components/ui/useToast'
import {
  Users, ArrowRightLeft, Shield, ShieldOff,
  UserCog, Building, Search, CheckCircle, XCircle
} from 'lucide-react'

export function AdminUsersPage() {
  const [user, setUser] = useState<User | null>(null)
  const [userId, setUserId] = useState('')
  const [targetTenantId, setTargetTenantId] = useState('')
  const [targetStudioId, setTargetStudioId] = useState('')
  const [result, setResult] = useState<string>('')
  const { notify } = useToast()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const sessionRes = await fetch('/api/admin/session', { credentials: 'include' })
        if (sessionRes.ok) {
          const sessionData = await sessionRes.json()
          if (sessionData.success && sessionData.data?.user) {
            setUser(sessionData.data.user)
          }
        }
      } catch {
        // Silent fail
      }
    })()
  }, [])

  const move = async () => {
    const uid = parseInt(userId, 10)
    const tid = parseInt(targetTenantId, 10)
    const sid = targetStudioId ? parseInt(targetStudioId, 10) : undefined

    if (!Number.isFinite(uid) || !Number.isFinite(tid)) {
      notify({ kind: 'error', message: 'Please enter valid User ID and Tenant ID' })
      return
    }

    setLoading(true)
    const res = await adminAPI.moveUser({ user_id: uid, target_tenant_id: tid, target_studio_id: sid })
    setLoading(false)

    if (res.success) {
      setResult('✓ User moved successfully')
      notify({ kind: 'success', message: 'User moved to new tenant' })
      setUserId('')
      setTargetTenantId('')
      setTargetStudioId('')
    } else {
      const msg = res.message || 'Failed to move user'
      setResult(`✗ ${msg}`)
      notify({ kind: 'error', message: msg })
    }
  }

  const grantSuperAdmin = async () => {
    const uid = parseInt(userId, 10)
    if (!Number.isFinite(uid)) {
      notify({ kind: 'error', message: 'Enter a valid user ID' })
      return
    }
    if (!confirm('⚠️ Grant SuperAdmin access? This gives platform-wide administrative privileges.')) return

    setLoading(true)
    const res = await adminAPI.setUserRole({ user_id: uid, role: 'SuperAdmin' })
    setLoading(false)

    if (res.success) {
      notify({ kind: 'success', message: 'SuperAdmin access granted successfully' })
      setResult('✓ SuperAdmin access granted')
    } else {
      notify({ kind: 'error', message: res.message || 'Failed to grant SuperAdmin' })
      setResult(`✗ ${res.message || 'Failed'}`)
    }
  }

  const revokeSuperAdmin = async () => {
    const uid = parseInt(userId, 10)
    if (!Number.isFinite(uid)) {
      notify({ kind: 'error', message: 'Enter a valid user ID' })
      return
    }
    if (!confirm('Revoke SuperAdmin access? This user will be demoted to regular Admin.')) return

    setLoading(true)
    const res = await adminAPI.setUserRole({ user_id: uid, role: 'Admin' })
    setLoading(false)

    if (res.success) {
      notify({ kind: 'success', message: 'SuperAdmin access revoked' })
      setResult('✓ SuperAdmin access revoked')
    } else {
      notify({ kind: 'error', message: res.message || 'Failed to revoke SuperAdmin' })
      setResult(`✗ ${res.message || 'Failed'}`)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
          User Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
          <Users className="h-4 w-4" />
          Move users between tenants and manage global permissions
        </p>
      </div>

      {/* Move User Section */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-lg">Move User Between Tenants</h2>
        </div>
        <div className="card-body">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Transfer a user to a different tenant organization. This will update their tenant_id and studio_id.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="form-label flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-500" />
                User ID
              </label>
              <Input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter user ID"
                type="number"
              />
            </div>
            <div>
              <label className="form-label flex items-center gap-2">
                <Building className="h-4 w-4 text-gray-500" />
                Target Tenant ID
              </label>
              <Input
                value={targetTenantId}
                onChange={(e) => setTargetTenantId(e.target.value)}
                placeholder="New tenant ID"
                type="number"
              />
            </div>
            <div>
              <label className="form-label flex items-center gap-2">
                <Building className="h-4 w-4 text-gray-500" />
                Target Studio ID (optional)
              </label>
              <Input
                value={targetStudioId}
                onChange={(e) => setTargetStudioId(e.target.value)}
                placeholder="Leave blank for auto"
                type="number"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button onClick={move} disabled={loading} className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4" />
              {loading ? 'Moving...' : 'Move User'}
            </Button>
            {result && (
              <div className={`flex items-center gap-2 text-sm font-medium ${
                result.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {result.startsWith('✓') ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                {result}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SuperAdmin Management */}
      <div className="card border-orange-200 dark:border-orange-800 bg-gradient-to-br from-orange-50/50 to-red-50/50 dark:from-orange-500/5 dark:to-red-500/5">
        <div className="card-header flex items-center gap-2">
          <Shield className="h-5 w-5 text-orange-600" />
          <h2 className="font-semibold text-lg">SuperAdmin Access Control</h2>
        </div>
        <div className="card-body">
          <div className="p-4 bg-orange-100 dark:bg-orange-500/10 border border-orange-300 dark:border-orange-700 rounded-lg mb-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-orange-700 dark:text-orange-400 mt-0.5" />
              <div className="flex-1 text-sm">
                <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-1">⚠️ Critical Permissions</h3>
                <p className="text-orange-800 dark:text-orange-300">
                  SuperAdmin role grants full platform access including tenant management, user controls, and system settings.
                  Only assign to trusted individuals.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="form-label flex items-center gap-2">
                <UserCog className="h-4 w-4 text-gray-500" />
                Target User ID
              </label>
              <Input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter user ID"
                type="number"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={grantSuperAdmin}
                disabled={loading}
                className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              >
                <Shield className="h-4 w-4" />
                {loading ? 'Processing...' : 'Grant SuperAdmin'}
              </Button>
              <Button
                onClick={revokeSuperAdmin}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ShieldOff className="h-4 w-4" />
                {loading ? 'Processing...' : 'Revoke SuperAdmin'}
              </Button>
            </div>

            {result && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                result.startsWith('✓')
                  ? 'bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800'
              }`}>
                {result.startsWith('✓') ? (
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                )}
                <span className={`text-sm font-medium ${
                  result.startsWith('✓') ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                }`}>
                  {result}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="card bg-blue-50 dark:bg-blue-500/5 border-blue-200 dark:border-blue-800">
        <div className="card-body">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Quick Tips</h3>
          <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400">•</span>
              <span>User IDs can be found in the database or through the API</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400">•</span>
              <span>When moving users, leave Studio ID blank to auto-assign to the tenant's first studio</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400">•</span>
              <span>SuperAdmin role bypasses all tenant-level restrictions</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
